import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  InputAdornment,
  ListItem,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AppState, useAppDispatch, useAppSelector } from "../../store";
import {
  setIsEditRole,
  setOpenRolesModal,
  setSelectRoleData,
} from "../../store/slices/usersSlice";
import { useRoles } from "../../hooks/useRoles";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useUsers } from "../../hooks/useUsers";
import { setUserPermissions } from "../../store/slices/commonSlice";
import { AlertDialog } from "../common/AlertDailog";
import { rolesCreationSchema } from "../../config/schemas/roleSchema";
import DynamicForm from "../common/DynamicForm";
import FormFields from "./roleFields.json";
import SearchIcon from "@mui/icons-material/Search";
import { usePermissionWrapper } from "../../hooks/usePermissionWrapper";
import AddReasonForm from "../common/AddReasonForm";
import { useGetChangedFields } from "../../hooks/useGetChangedFields";

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

const RolesModal: React.FunctionComponent<any> = (props) => {
  const { getAllRolesFunc, paginationModel } = props;
  const dispatch = useAppDispatch();
  const { checkPermission } = usePermissionWrapper();
  const storeSelectedRoleData = useAppSelector(
    (state: AppState) => state.usersSlice.selectRoleData
  );
  const isEditRole = useAppSelector(
    (state: AppState) => state.usersSlice.isEditRole
  );
  const initialData = {
    code: "",
    name: "",
    description: "",
  };

  const {
    getAllRoles,
    getSelectedRoles,
    selectedRoleData,
    selectedRoleIsLoading,
    selectedRoleSuccess,
    selectedRoleError,
    selectedRoleIsError,
  } = useRoles();

  const values = isEditRole ? storeSelectedRoleData : initialData;
  type FormData = z.infer<typeof rolesCreationSchema>;
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    getValues,
  } = useForm({
    defaultValues: initialData,
    values,
    resolver: zodResolver(rolesCreationSchema),
  });

  const createCode = watch("code");
  const name = watch("name");
  const desc = watch("description");

  const openRolesModal = useAppSelector(
    (state: AppState) => state.usersSlice?.openRolesModal
  );

  const { showSuccess, handleError, showError } = useSnackbar();
  const {
    createRoleFunc,
    createRoleSuccess,
    createRoleData,
    createRoleIsError,
    createRoleError,
    updateRole,
    updateRoleSuccess,
    updateRoleData,
    updateRoleIsError,
    updateRoleError,
  } = useRoles();

  const [modules, setModules] = useState(storeSelectedRoleData.modules);
  const [searchText, setSearchText] = useState<any>();

  const initialPermissionCount: any = {};
  if (isEditRole) {
    for (const i of storeSelectedRoleData.modules) {
      const temp = i.module;
      initialPermissionCount[temp] = i["assigned_count"];
    }
  } else if (modules && Object.keys(modules).length > 0) {
    for (const i of modules) {
      const temp = i.module;
      initialPermissionCount[temp] = i["assigned_count"];
    }
  }

  const [updatedPermissions, setUpdatedPermissions] = useState<any>({
    added: {},
    removed: {},
  });

  const [permissionsCount, setPermissionsCount] = useState<any>(
    initialPermissionCount
  );
  const [toggleState, setToggleState] = useState("");
  const [removePermissionsAlert, setRemovePermissionsAlert] = useState(false);

  const fieldsChanged =
    Object.values(updatedPermissions.added)?.flat().length > 0 ||
    Object.values(updatedPermissions.removed)?.flat().length > 0 ||
    name != values?.name ||
    desc != values?.description;

  const submit = (data: FormData, updatedDataObj?: any) => {
    if (fieldsChanged) {
      if (isEditRole) {
        const temp = {
          code: data.code,
          name: data.name,
          description: data.description,
          permissions: {
            added: Object.values(updatedPermissions.added)?.flat() ?? [],
            removed: Object.values(updatedPermissions.removed)?.flat() ?? [],
          },
        };
        const dataObj = checkPermission("OVERRIDE_AUDIT_REASON")
          ? temp
          : { ...temp, updated_fields: updatedDataObj };
        updateRole({
          data: dataObj,
        });
      } else {
        const addedPermissions = [
          ...Object.values(updatedPermissions.added).flat(),
          ...Object.values(updatedPermissions.removed).flat(),
        ];
        if (addedPermissions?.length == 0) {
          showError("Minimum one permission should be added");
          return;
        }
        createRoleFunc({
          data: {
            code: data.code,
            name: data.name,
            description: data.description,
            permissions: addedPermissions,
          },
        });
      }
    } else {
      showError(`No data modified`);
    }
  };
  const onInvalid = (errors: any) => console.debug(errors);
  const handleClose = () => {
    dispatch(setOpenRolesModal(false));
    dispatch(setIsEditRole(false));
    dispatch(setSelectRoleData({}));
    reset();
  };

  const { getAllUIPermissions, uIPermissionData, uIPermissionIsSuccess } =
    useUsers();

  useEffect(() => {
    if (uIPermissionIsSuccess) {
      dispatch(
        setUserPermissions(
          uIPermissionData?.user_permissions?.map((item: any) => item.name)
        )
      );
    }
  }, [uIPermissionData, uIPermissionIsSuccess]);

  useEffect(() => {
    if (createRoleSuccess || updateRoleSuccess) {
      const successMsg = createRoleData ?? updateRoleData;
      getAllRolesFunc(0, paginationModel?.pageSize);
      const id = createRoleSuccess
        ? "role-create-success"
        : "role-update-success";
      showSuccess(successMsg?.details, id);
      if (updateRoleSuccess) {
        getAllUIPermissions({});
      }
      handleClose();
    }
  }, [createRoleSuccess, updateRoleSuccess]);

  useEffect(() => {
    if (createRoleIsError || updateRoleIsError) {
      const errorMsg: any = createRoleError ?? updateRoleError;
      handleError(errorMsg);
    }
  }, [createRoleIsError, updateRoleIsError]);

  const [value, setValue] = React.useState(0);
  const [activeTab, setActiveTab] = useState(
    storeSelectedRoleData.modules?.[0]?.module
  );

  const handleChange = (event: any, newValue: number) => {
    setActiveTab(event?.target?.id);
    setValue(newValue);
  };

  const label = {
    inputProps: { "aria-label": "All permissions in this Module" },
  };

  const steps = ["Add Role Details", "Assign Permissions"];

  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = async () => {
    if (!isEditRole) {
      const res = await getAllRoles({});
      const roleCodesList = res.data?.result?.map((item: any) => item.code);
      if (
        roleCodesList?.find(
          (item: any) => item.toUpperCase() == createCode.toUpperCase()
        )
      ) {
        showError("Record already exists");
        return;
      } else {
        const temp = await getSelectedRoles({ code: createCode });
        if (temp?.data) {
          setModules(temp?.data?.module_count);
          setPermissions({
            assigned: temp?.data.permissions.assigned_permissions,
            unassigned: temp?.data.permissions.unassigned_permissions,
          });
          setAssignedPermissions(
            temp?.data.permissions.assigned_permissions?.map(
              (item: any) => item.code
            )
          );
          setUnAssignedPermissions(
            temp?.data.permissions.unassigned_permissions?.map(
              (item: any) => item.code
            )
          );
        }
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const [permissions, setPermissions] = useState<any>({
    assigned: storeSelectedRoleData?.assigned_permissions,
    unassigned: storeSelectedRoleData?.unassigned_permissions,
  });

  const [assignedPermissions, setAssignedPermissions] = useState(
    storeSelectedRoleData?.assigned_permissions?.map(
      (item: { code: any }) => item.code
    )
  );
  const [unAssignedPermissions, setUnAssignedPermissions] = useState(
    storeSelectedRoleData?.unassigned_permissions?.map(
      (item: { code: any }) => item.code
    )
  );
  const handleToggleTab = async (data: any) => {
    setActiveTab(data?.module);
    if (
      Object.keys(permissionsCount).length == 0 ||
      !Object.keys(permissionsCount)?.includes(data?.module) ||
      permissionsCount?.[data.module] == 0
    ) {
      setToggleState("all");
    } else if (permissionsCount?.[data.module] > 0) {
      setRemovePermissionsAlert(true);
    }
  };

  const getTabPermissions = async (tab: any, toggle?: any) => {
    const res: any = await getSelectedRoles({
      code: storeSelectedRoleData.code || createCode,
      module: tab,
    });
    if (res?.data) {
      const temp = [
        ...res.data.permissions.assigned_permissions.map(
          (item: any) => item.code
        ),
        ...res.data.permissions.unassigned_permissions.map(
          (item: any) => item.code
        ),
      ];
      if (toggle == "all") {
        setPermissions({
          assigned: res.data.permissions.assigned_permissions,
          unassigned: res.data.permissions.unassigned_permissions,
        });
        setAssignedPermissions(temp);
        setUnAssignedPermissions([]);
        setToggleState("");
        const count = { ...permissionsCount };
        count[activeTab] = temp?.length;
        setPermissionsCount(count);
        const a: any = {};
        a[activeTab] = 0;
        setUpdatedPermissions({
          ...updatedPermissions,
          removed: { ...updatedPermissions.removed, ...a },
        });
        return;
      }
      if (toggle == "none") {
        setPermissions({
          assigned: res.data.permissions.assigned_permissions,
          unassigned: res.data.permissions.unassigned_permissions,
        });
        setAssignedPermissions([]);
        setUnAssignedPermissions(temp);
        setToggleState("");
        const count = { ...permissionsCount };
        count[activeTab] = 0;
        setPermissionsCount(count);
        setRemovePermissionsAlert(false);
        const a: any = {};
        a[activeTab] = [];
        setUpdatedPermissions({
          ...updatedPermissions,
          added: { ...updatedPermissions.added, ...a },
        });
        return;
      }
    } else {
      setPermissions({});
      setAssignedPermissions([]);
      setUnAssignedPermissions([]);
    }
  };

  useEffect(() => {
    if (activeTab && toggleState) {
      getTabPermissions(activeTab, toggleState);
    }
  }, [toggleState]);

  useEffect(() => {
    if (!toggleState && (storeSelectedRoleData.code || createCode)) {
      (async () => {
        const res: any = await getSelectedRoles({
          code: storeSelectedRoleData.code || createCode,
          module: activeTab,
        });
        if (res?.data) {
          setPermissions({
            assigned: res.data.permissions.assigned_permissions,
            unassigned: res.data.permissions.unassigned_permissions,
          });
          setAssignedPermissions(
            res.data.permissions.assigned_permissions?.map(
              (item: any) => item.code
            )
          );
          setUnAssignedPermissions(
            res.data.permissions.unassigned_permissions?.map(
              (item: any) => item.code
            )
          );
          if (!isEditRole) {
            setModules(res?.data?.module_count);
          }
        } else {
          setPermissions({});
          setAssignedPermissions([]);
          setUnAssignedPermissions([]);
        }
      })();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!selectedRoleIsLoading && selectedRoleSuccess) {
      if (selectedRoleData && !isEditRole) {
        return;
        setModules(selectedRoleData?.module_count);
        setPermissions({
          assigned: selectedRoleData.permissions.assigned_permissions,
          unassigned: selectedRoleData.permissions.unassigned_permissions,
        });
        setAssignedPermissions(
          selectedRoleData.permissions.assigned_permissions?.map(
            (item: any) => item.code
          )
        );
        setUnAssignedPermissions(
          selectedRoleData.permissions.unassigned_permissions?.map(
            (item: any) => item.code
          )
        );
      }
    }
  }, [selectedRoleIsLoading, selectedRoleSuccess]);

  useEffect(() => {
    if (selectedRoleIsError) {
      setPermissions({});
      setAssignedPermissions([]);
      setUnAssignedPermissions([]);
      const error: any = selectedRoleError;
      handleError(error);
    }
  }, [selectedRoleIsError]);

  const handlePermissionChange = (code: any) => {
    if (assignedPermissions?.includes(code)) {
      setAssignedPermissions(
        assignedPermissions?.filter((item: any) => item != code)
      );
      setUnAssignedPermissions([...unAssignedPermissions, code]);
      const temp: any = {};
      temp[activeTab] = updatedPermissions.added?.[activeTab]?.filter(
        (i: any) => i != code
      );
      setUpdatedPermissions({
        ...updatedPermissions,
        added: { ...updatedPermissions.added, ...temp },
      });
      const count = { ...permissionsCount };
      count[activeTab] = assignedPermissions?.filter(
        (item: any) => item != code
      )?.length;
      setPermissionsCount(count);
      return;
    }
    if (unAssignedPermissions?.includes(code)) {
      setUnAssignedPermissions(
        unAssignedPermissions?.filter((item: any) => item != code)
      );
      setAssignedPermissions([...assignedPermissions, code]);
      const temp: any = {};
      temp[activeTab] = updatedPermissions.removed?.[activeTab]?.filter(
        (i: any) => i != code
      );
      setUpdatedPermissions({
        ...updatedPermissions,
        removed: { ...updatedPermissions.removed, ...temp },
      });
      const count = { ...permissionsCount };
      count[activeTab] = [...assignedPermissions, code]?.length;
      setPermissionsCount(count);
      return;
    }
  };

  useEffect(() => {
    const temp = permissions?.assigned?.map((i: any) => i.code);
    const unique1 = assignedPermissions?.filter(
      (value: any) => !temp.includes(value)
    );
    const unique2 = temp?.filter(
      (value: any) => !assignedPermissions.includes(value)
    );
    const unique = Array.from(new Set(unique1?.concat(unique2)));
    const a = { ...updatedPermissions };
    a.added[activeTab] = a.added[activeTab]
      ? [
          ...a.added[activeTab],
          ...unique.filter(
            (code: any) =>
              !temp?.includes(code) && !a.added?.[activeTab]?.includes(code)
          ),
        ]
      : unique?.filter(
          (code: any) =>
            !temp?.includes(code) && !a.added?.[activeTab]?.includes(code)
        );
    a.removed[activeTab] = a.removed[activeTab]
      ? [
          ...a.removed[activeTab],
          ...unique.filter(
            (code: any) =>
              temp?.includes(code) && !a.removed?.[activeTab]?.includes(code)
          ),
        ]
      : unique?.filter(
          (code: any) =>
            temp?.includes(code) && !a.removed?.[activeTab]?.includes(code)
        );
    setUpdatedPermissions(a);
  }, [assignedPermissions]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setDrawerOpen(open);
    };

  const editedData: any = useGetChangedFields(
    control,
    watch,
    FormFields?.editRoleFields,
    getValues,
    values
  );

  const handleAddReason = (updatedDataObj: any) => {
    submit(getValues(), updatedDataObj);
  };

  const [selectedRolePermissions, setSelectedRolePermissions] = useState<any>({
    assigned: [],
    unassigned: [],
  });

  useEffect(() => {
    if (!checkPermission("OVERRIDE_AUDIT_REASON")) {
      (async () => {
        const res: any = await getSelectedRoles({
          code: storeSelectedRoleData.code || createCode,
          module: "All",
        });
        if (res?.data) {
          setSelectedRolePermissions({
            assigned: res?.data?.permissions?.assigned_permissions,
            unassigned: res?.data?.permissions?.unassigned_permissions,
          });
        }
      })();
    }
  }, []);

  const permissionEditedData = [
    {
      id: "permissions",
      field: "Permissions",
      newData: [
        ...selectedRolePermissions.assigned
          .filter(
            (item: any) =>
              !Object.values(updatedPermissions.removed)
                .flat()
                .includes(item.code)
          )
          .map((i: any) => i.name),
        ...selectedRolePermissions.unassigned
          .filter((item: any) =>
            Object.values(updatedPermissions.added).flat().includes(item.code)
          )
          .map((i: any) => i.name),
      ],
      oldData: selectedRolePermissions?.assigned?.map((item: any) => item.name),
    },
  ];

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
  };

  const [filteredPermissions, setFilteredPermissions] = useState(permissions);

  useEffect(() => {
    setFilteredPermissions(permissions);
  }, [permissions]);

  useEffect(() => {
    if (searchText) {
      const assigned = permissions?.assigned?.filter((item: any) =>
        item.code
          .toLowerCase()
          .includes(
            searchText.toLowerCase() ||
              item.description.toLowerCase().includes(searchText.toLowerCase())
          )
      );

      const unassigned = permissions?.unassigned?.filter((item: any) =>
        item.code
          .toLowerCase()
          .includes(
            searchText.toLowerCase() ||
              item.description.toLowerCase().includes(searchText.toLowerCase())
          )
      );
      const temp = { assigned, unassigned };
      setFilteredPermissions(temp);
    } else {
      setFilteredPermissions(permissions);
    }
  }, [searchText]);

  const editedPermissionsList = [
    ...Object.values(updatedPermissions.added).flat(),
    ...Object.values(updatedPermissions.removed).flat(),
  ];

  return (
    <>
      <AlertDialog
        open={openRolesModal}
        title={
          activeStep == 0
            ? isEditRole
              ? `Edit Role Details`
              : `Add Role Details`
            : "Assign Permissions"
        }
        handleClose={handleClose}
        handleConfirm={
          activeStep == 0
            ? handleSubmit(handleNext)
            : !checkPermission("OVERRIDE_AUDIT_REASON") && isEditRole
            ? toggleDrawer(true)
            : handleSubmit(submit, onInvalid)
        }
        cancelBtn="CANCEL"
        confirmBtn={activeStep == 0 ? "Next" : "Save"}
        maxWidth="xl"
        saveDisable={
          activeStep != 0 &&
          editedData?.length == 0 &&
          editedPermissionsList.length == 0
        }
      >
        <Box sx={{ height: "68vh" }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          {activeStep === 0 ? (
            <>
              <Box mt={1}>
                <form onSubmit={handleSubmit(submit, onInvalid)}>
                  <DynamicForm
                    fieldsJson={
                      isEditRole
                        ? FormFields?.editRoleFields
                        : FormFields?.createRoleFields
                    }
                    errors={errors}
                    register={register}
                    control={control}
                  />
                </form>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                bgcolor: "background.paper",
                display: "flex",
                '& div[role="tabpanel"]': { flex: 1 },
                mt: 3,
                maxHeight: "calc(67vh - 44px)",
              }}
            >
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={value}
                onChange={handleChange}
                aria-label="Vertical tabs example"
                sx={{
                  borderRight: 2,
                  borderColor: "divider",
                  flex: "0 0 240px",
                  "& .MuiTab-labelIcon": { minHeight: "48px" },
                  "& .MuiTab-root": {
                    justifyContent: "space-between",
                    pr: 1,
                    textAlign: "left",
                    '&[aria-selected="true"]': {
                      background: "rgba(36, 63, 129, 0.07)",
                    },
                  },
                }}
              >
                {modules?.map((item: any) => (
                  <Tab
                    label={item.module}
                    {...a11yProps(0)}
                    key={item.module}
                    id={item.module}
                    icon={
                      <>
                        <Box
                          className="dFlex"
                          columnGap={1}
                          alignItems="center"
                        >
                          <span className="fs12">
                            {permissionsCount?.[item.module] ??
                              item.assigned_count}
                            /<span className="fw400">{item.total_count}</span>
                          </span>
                          <Tooltip
                            title={`Toggle switch to allow all permissions under ${item.module}`}
                            placement="top"
                          >
                            <Switch
                              {...label}
                              checked={permissionsCount?.[item.module] > 0}
                              size="small"
                              color={
                                permissionsCount?.[item.module] ==
                                item.total_count
                                  ? "primary"
                                  : permissionsCount?.[item.module] > 0 &&
                                    permissionsCount?.[item.module] !=
                                      item.total_count
                                  ? "warning"
                                  : undefined
                              }
                              id={item.module}
                              onChange={() => {
                                handleToggleTab(item);
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </>
                    }
                    iconPosition="end"
                  />
                ))}
              </Tabs>

              <Box sx={{ pl: 3, width: "100%" }}>
                <Grid container spacing={0} columnSpacing={3} mb={3}>
                  <Grid item md={9}>
                    &nbsp;
                  </Grid>
                  <Grid item md={3}>
                    <TextField
                      id="module-search"
                      type="search"
                      variant="standard"
                      placeholder="Search permission"
                      size="small"
                      fullWidth
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    minHeight: "50vh",
                    maxHeight: "calc(67vh - 100px)",
                    overflowY: "auto",
                    pr: 1,
                    mr: -1,
                  }}
                >
                  <Grid container columnSpacing={3}>
                    {filteredPermissions &&
                      Object.values(filteredPermissions)
                        ?.flat()
                        ?.map((item: any) => (
                          <Grid item md={6} key={item?.code}>
                            <ListItem
                              secondaryAction={
                                <Switch
                                  {...label}
                                  size="small"
                                  checked={
                                    (assignedPermissions?.includes(
                                      item?.code
                                    ) &&
                                      !Object.values(updatedPermissions.removed)
                                        .flat()
                                        ?.includes(item?.code)) ||
                                    Object.values(updatedPermissions?.added)
                                      .flat()
                                      .includes(item?.code)
                                  }
                                  onChange={() =>
                                    handlePermissionChange(item?.code)
                                  }
                                />
                              }
                              sx={{
                                "& .MuiListItemSecondaryAction-root": {
                                  top: "30%",
                                },
                              }}
                            >
                              <ListItemText
                                primary={item?.name}
                                sx={{
                                  "& .MuiListItemText-primary": {
                                    fontWeight: 500,
                                  },
                                }}
                                secondary={
                                  <>
                                    <Tooltip
                                      title={
                                        item?.description.length > 58
                                          ? item?.description
                                          : null
                                      }
                                      placement="bottom"
                                    >
                                      <Typography
                                        variant="body2"
                                        className="textTruncate pt1"
                                        gutterBottom
                                        sx={{ maxWidth: "500px" }}
                                      >
                                        {item?.description}
                                      </Typography>
                                    </Tooltip>
                                  </>
                                }
                              />
                            </ListItem>
                            <Divider />
                          </Grid>
                        ))}
                  </Grid>
                </Box>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Button
              className="grey-btn"
              onClick={handleBack}
              sx={{ mr: 1, position: "absolute", bottom: "8px" }}
            >
              Back
            </Button>
          )}
        </Box>
        <div
          id="drawer-container"
          style={{ display: drawerOpen ? "block" : "none" }}
        />
        {drawerOpen && (
          <Drawer
            anchor="bottom"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            style={{ zIndex: "1300" }}
            PaperProps={{ style: { position: "absolute" } }}
            ModalProps={{
              container: document.getElementById("drawer-container"),
              style: { position: "absolute", background: "rgba(0,0,0,0.3)" },
            }}
          >
            <AddReasonForm
              toggleDrawer={toggleDrawer}
              editedData={
                editedPermissionsList?.length != 0
                  ? [...editedData, ...permissionEditedData]
                  : editedData
              }
              handleAddReason={handleAddReason}
            />
          </Drawer>
        )}
      </AlertDialog>
      <AlertDialog
        open={removePermissionsAlert}
        title="Remove Permission"
        description={`Are you sure you want to remove the permissions from <strong>${activeTab}</strong> module?`}
        handleClose={() => setRemovePermissionsAlert(false)}
        handleConfirm={() => setToggleState("none")}
        confirmBtn="Yes"
        cancelBtn="No"
        maxWidth="xs"
      />
    </>
  );
};

export default RolesModal;
